import { format } from "date-fns";
import { Database, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import type { KnowledgeBase } from "@/services/knowledge-base.service";
import { counted, formatCount } from "@/utils/kb-upload-rules";

interface KnowledgeBaseCardProps {
  kb: KnowledgeBase;
  /** The plan's files per knowledge base. */
  maxFiles: number;
  canDelete: boolean;
  onOpen: () => void;
  onDelete: () => void;
}

/** One knowledge base in the list: its name, its files and their states, its description. */
export function KnowledgeBaseCard({ kb, maxFiles, canDelete, onOpen, onDelete }: KnowledgeBaseCardProps) {
  const states = [
    [kb.ready_count, "ready"],
    [kb.processing_count, "processing"],
    [kb.error_count, "failed"],
  ] as const;
  return (
    <Card data-testid="kb-card" className="relative border-2 hover:border-primary/50">
      <CardHeader className="flex-row items-start justify-between space-y-0 pb-3">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <Database className="h-6 w-6 text-primary" />
        </div>
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="relative z-10 -mr-2 h-8 w-8 text-muted-foreground hover:text-destructive"
            aria-label={`Delete ${kb.display_name}`}
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        <h3 className="truncate text-lg font-semibold">
          {/* The whole card opens it: the button's ::after covers the card, the delete button sits above. */}
          <button type="button" onClick={onOpen} title={kb.display_name} className="text-left after:absolute after:inset-0">
            {kb.display_name}
          </button>
        </h3>
        <p className="text-sm text-muted-foreground">
          {formatCount(kb.file_count - kb.error_count)} of {counted(maxFiles, "file")}
          {states
            .filter(([n]) => n > 0)
            .map(([n, state]) => ` · ${formatCount(n)} ${state}`)
            .join("")}
        </p>
        <div className="flex items-start gap-1.5 rounded-md bg-muted/30 p-2">
          <Sparkles className="mt-0.5 h-3 w-3 flex-shrink-0 text-primary" />
          <p className="line-clamp-2 break-words text-xs">{kb.tool_description ?? "No description yet"}</p>
        </div>
      </CardContent>
      <CardFooter className="pt-0 text-xs text-muted-foreground">
        Updated {format(new Date(kb.updated_at), "MMM d, yyyy")}
      </CardFooter>
    </Card>
  );
}
